import React, { useEffect, useState } from "react"
import css from '../../NfpStyles'
import { Box, Divider, Grid2 as Grid, Typography } from '@mui/material'
import DaggerIcon from '../../../img/DaggerIcon';
import { getLabel } from '../LdsEntryPage';
import { getNamedNutrients, isSelected } from '../../NfpUtils'

let css2 = {
  gridContainer: {
    width: '100%',
    whiteSpace: 'nowrap'
  },
  col1: {
    display: 'inline-block',
    width: '44%',
  },
  col1Content: {
    width: 'calc(100% - .5em)',
    marginRight: '.5em',
  },
  col2: {
    display: 'inline-block',
    width: '28%',
    borderLeft: '1px solid #CCC',
    minHeight: '16px'
  },
  col2Content: {
    width: 'calc(100% - 1em)',
    marginRight: '.5em',
    marginLeft: '.5em',
  },
  col3: {
    display: 'inline-block',
    width: '28%',
    borderLeft: '1px solid #CCC',
  },
  col3Content: {
    width: 'calc(100% - .5em)',
    marginLeft: '.5em'
  },

  nutritionFactPanelVertical: {
    maxWidth: '514px',
    pageBreakInside: 'avoid',
    breakInside: 'avoid'
  },
  alignRight: {
    textAlign: 'right'
  },
  av: {
    minHeight: '13px'
  },
  bold: {
    fontWeight: 900
  },
  caloriesH2: {
    lineHeight: 1,
    textAlign: 'right'
  },
  caloriesH3: {
    lineHeight: 1
  },

  colXs6: {
    position: 'relative',
    minHeight: '1px',
    paddingRight: '15px',
    paddingLeft: '15px',
    float: 'left',
    width: '48%'
  },
  column1: {
    flexBasis: '100%',
    paddingLeft: 0
  },
  columnNutrientAv: {
    margin: '0.5em 0 0 0.5em',
    paddingTop: '0.5em',
    fontSize: '11px'
  },
  columnNutrientDv: {
    margin: '0.5em 0 0 0.5em',
    paddingTop: '0.5em',
    fontSize: '11px',
    float: 'none'
  },
  dv: {
    float: 'right',
    minHeight: '13px',
    verticalAlign: 'top'
  },
  dvBold: {
    float: 'right',
    minHeight: '13px',
    verticalAlign: 'top',
    fontWeight: 900
  },
  h5: {
    fontSize: '12px',
    fontFamily: 'inherit',
    lineHeight: 1.1,
    verticalAlign: 'top',
    color: '#000000',
    fontWeight: 400,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    letterSpacing: 'normal',
    textAlign: 'left',
    width: '100%',
    display: 'inline',
    verticalAlign: 'top'
  },
  m003: {
    marginTop: '0px',
    marginRight: '0px',
    marginBottom: '3px',
    marginLeft: '0px'
  },  
  nutrientName: {
    whiteSpace: 'nowrap',
    fontSize: '11px'
  },
  servingSize: {
    lineHeight: 1.3
  },
  significantSourceText: {
    marginTop: '4px',
    display: 'block'
  }
}

export default function NfpDualColumnView ({nfp1 = {}, nfp2 = {}, lds = {}, significantSourceText}) {
  lds.serve = lds.serve ?? 'single'
  lds.isAbbreviated = lds.isAbbreviated ?? false
  lds.isSimplified = lds.isSimplified ?? false

  const microNutrientNotSignificantSourceRules = {
    '84a8709a-0026-0000-ebf9-90cea7d9d44f': (nutrient) => !lds.isSimplified || parseFloat(nutrient.percent) >= 2 || nutrient.percentState === 'Citation', //showVitaminD
    '84a8709a-002f-0000-ebf9-90cea7d9d44f': (nutrient) => !lds.isSimplified || parseFloat(nutrient.percent) >= 2 || nutrient.percentState === 'Citation', //showCalcium
    '84a8709a-0035-0000-ebf9-90cea7d9d44f': (nutrient) => !lds.isSimplified || parseFloat(nutrient.percent) >= 2 || nutrient.percentState === 'Citation', //showIron
    '84a8709a-003a-0000-ebf9-90cea7d9d44f': (nutrient) => !lds.isSimplified || parseFloat(nutrient.percent) >= 2 || nutrient.percentState === 'Citation', //showPotassium
  }

  const microNutrients = [...Object.keys(nfp1)].reduce((microNutrients, id) => {
    let n1 = nfp1[id] ?? {}
    let n2 = nfp2[id] ?? {}
    if(n1.isMicroNutrient && (isSelected(n1) || isSelected(n2)) && (!microNutrientNotSignificantSourceRules[id] || microNutrientNotSignificantSourceRules[id](n1))) {
      microNutrients.push({n1, n2})
    }
    return microNutrients
  }, [])

  let i = 0
  const n1 = getNamedNutrients(nfp1)
  const n2 = getNamedNutrients(nfp2)

  //Show if > 2% DV
  const showSaturatedFat = !lds.isSimplified || parseFloat(n1.satFat.percent) >= 2
  const showTransFat = !lds.isSimplified || parseFloat(n1.transFat.percent) >= 2
  const showPolyFat = !lds.isSimplified || parseFloat(n1.polyFat.percent) >= 2 
  const showMonoFat = !lds.isSimplified || parseFloat(n1.monoFat.percent) >= 2
  const showCholesterol = !lds.isSimplified || parseFloat(n1.cholesterol.percent) >= 2
  const showDietaryFiber = !lds.isSimplified || parseFloat(n1.dietaryFiber.percent) >= 2
  const showTotalSugars = !lds.isSimplified || lds.isSimplifiedWithSugar || parseFloat(n1.totalSugars.value) >= 1
  const showAddedSugars = !lds.isSimplified || lds.isSimplifiedWithSugar || parseFloat(n1.addedSugars.percent) >= 2

  const [showCitation, setShowCitation] = useState(false)

  useEffect(() => {
    let newShowCitation = [...Object.keys(nfp1)].reduce((showCitation, id) => {
      return showCitation || nfp1[id].percentState === 'Citation' || nfp2[id].percentState === 'Citation'
    }, false)
    setShowCitation(newShowCitation)
  }, [nfp1, nfp2])  



  const renderMicroNutrientRow = (nutrient1, nutrient2, isLast) => {
    const getValue = (nutrient) => {
      return (nutrient) ? (nutrient.percentState === 'Citation' ? <DaggerIcon style={{fontSize: '9px', position:'relative', top:'-2px', width: '9', height:'9'}}/> : (
        <>
          <Box component="span" style={{ ...css.av, ...css2.av }}>{ nutrient.amountState === 'Visible' ? `${nutrient.value ?? '0'}${nutrient.uom}` : ''}</Box>
          <Box component="span" style={{ ...css.dv, ...css2.dv }}>{ nutrient.percentState === 'Visible' ? `${nutrient.percent ?? '0'}%` : ''}</Box>
        </>)) : <></>
    }

    return <Box key={`nutrient${i}`} style={{ ...css2.gridContainer }}>
    <Box style={ css2.col1 }>
      <Box style={ css2.col1Content }>
        <Typography variant="span" style={ css2.nutrientName }>{getLabel(nutrient1, lds.isAbbreviated)}</Typography>
        {!isLast && <Divider style={{ ...css.hr, ...css.hr1}} />}
      </Box>
    </Box>
    <Box style={ css2.col2 }>
      <Box style={ css2.col2Content }>
        <Box component="span" style={{ ...css.av, ...css2.av }}>{getValue(nutrient1)}</Box>
        &nbsp;
        {!isLast && <Divider style={{ ...css.hr, ...css.hr1}} />}
      </Box>
    </Box>
    <Box style={ css2.col3 }>
      <Box style={ css2.col3Content }>
        <Box component="span" style={{ ...css.av, ...css2.av }}>{getValue(nutrient2)}</Box>
        &nbsp;
        {!isLast && <Divider style={{ ...css.hr, ...css.hr1}} />}
      </Box>
    </Box>
  </Box> 
    // <div key={`nutrient${i}`} style={{width: '100%', height: '1.05em', clear: 'both'}}>
    //     <div style={{...css2.nutrientName, float: 'left', width: '50%'}}>
    //       <Box component="span">{getLabel(nutrient1, lds.isAbbreviated)} {getValue(nutrient1)}</Box>
    //     </div>
    //     <div style={{ ...css2.nutrientName, position: 'relative', textAlign: 'right', float: 'right', width: '50%'}}>
    //       <Box component="span">{dot} {nutrient2 && getLabel(nutrient2, lds.isAbbreviated)} {getValue(nutrient2)}</Box>
    //     </div>
    // </div>
  }

  return (<>{nfp1 && 
    <Box id="nutrition-fact-panel-dual-column" style={{ ...css.nutritionFactPanel, ...css2.nutritionFactPanelVertical }}>
      <Typography variant="h1" style={css.h1}>Nutrition Facts</Typography>
      <Divider style={{...css.hr, ...css.hr1, ...css.mt0}} />
      {lds.serve === 'multi' &&
        <Box component="span">{lds.product?.servingsPerContainer} {'' + lds.product?.servingsPerContainer === '1' ? 'serving' : 'servings'} per container</Box>
      }

      <Box style={{ ...css.p0, ...css.m0, height: '1.1em'}}>
        <Box style={{width: '45%', float: 'left'}}>
          <Typography variant="h4" style={{ ...css.h4, ...css2.servingSize, verticalAlign: 'bottom'}}>{lds.isAbbreviated ? 'Serv.' : 'Serving'} Size</Typography>
        </Box>
        <Box style={{width: '45%', float: 'right'}}>
          <Typography variant="h4" style={{ ...css.h4, ...css.dvBold, lineHeight: '1.3', verticalAlign: 'bottom', textAlign: 'right' }}>{lds.product?.servingSize}</Typography>
        </Box>
      </Box>
      <div style={{ clear : 'both'}}/>
      <Divider style={{ ...css.hr, ...css.hr6, ...css.mb0}} />
        
      <Box style={ css2.gridContainer }>
        <Box style={ css2.col1 }>
          <Box style={ css2.col1Content }>
            <Typography variant="h3" style={{ ...css.h3, ...css2.caloriesH3 }}>Calories</Typography>
            <Divider style={{ ...css.hr, ...css.hr4 }}/>
          </Box>
        </Box>
        <Box style={ css2.col2 }>
          <Box style={ css2.col2Content }>
            <Typography variant="h2" style={{ ...css.h2, ...css2.caloriesH2 }}>{n1.calories.value}</Typography>
            <Divider style={{ ...css.hr, ...css.hr4 }}/>
          </Box>
        </Box>
        <Box style={ css2.col3 }>
          <Box style={ css2.col3Content }>
            <Typography variant="h2" style={{ ...css.h2, ...css2.caloriesH2 }}>{n2.calories.value ?? '0'}</Typography>
            <Divider style={{ ...css.hr, ...css.hr4 }}/>
          </Box>
        </Box>
      </Box>
      {/* <Box style={{ ...css2.gridContainer }}>
        <Box style={ css2.col1 }>
          <Box style={ css2.col1Content }>
          </Box>
        </Box>
        <Box style={ css2.col2 }>
          <Box style={ css2.col2Content }>
          </Box>
        </Box>
        <Box style={ css2.col3 }>
          <Box style={ css2.col3Content }>
          </Box>
        </Box>
      </Box> */}

      <Box style={{ ...css2.gridContainer }}>
        <Box style={ css2.col1 }>
          <Box style={ css2.col1Content }>
          </Box>
        </Box>
        <Box style={ css2.col2 }>
          <Box style={ css2.col2Content }>
            <Typography variant="h5" style={{ ...css.h5, ...css.dv }}>% Daily Value{lds.isSimplified ? '' : '*'}</Typography>
            <Divider style={{ ...css.hr, ...css.hr1 }}/>
          </Box>
        </Box>
        <Box style={ css2.col3 }>
          <Box style={ css2.col3Content }>
            <Typography variant="h5" style={{ ...css.h5, ...css.dv }}>% Daily Value{lds.isSimplified ? '' : '*'}</Typography>
            <Divider style={{ ...css.hr, ...css.hr1 }}/>
          </Box>
        </Box>
      </Box>
      <Box style={{ ...css2.gridContainer }}>
        <Box style={ css2.col1 }>
          <Box style={ css2.col1Content }>
            <Typography variant="h5" style={{ ...css2.h5, ...css.bold}}>Total Fat</Typography>
            <Divider style={{ ...css.hr, ...css.hr1}} />
          </Box>
        </Box>
        <Box style={ css2.col2 }>
          <Box style={ css2.col2Content }>
            {n1.totalFat.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.totalFat.value ?? '0'}g`}</Box>}
            {n1.totalFat.percentState === 'Visible' && <Box component="span" style={{ ...css.dv, ...css2.dvBold }}>{`${n1.totalFat.percent ?? '0'}%`}</Box>}
            &nbsp;
            <Divider style={{ ...css.hr, ...css.hr1}} />
          </Box>
        </Box>
        <Box style={ css2.col3 }>
          <Box style={ css2.col3Content }>
            {n2.totalFat.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.totalFat.value ?? '0'}g`}</Box>}
            {n2.totalFat.percentState === 'Visible' && <Box component="span" style={{ ...css.dv, ...css2.dvBold }}>{`${n2.totalFat.percent ?? '0'}%`}</Box>}
            &nbsp;
            <Divider style={{ ...css.hr, ...css.hr1}} />
          </Box>
        </Box>
      </Box>
      {!lds.isSimplified && showSaturatedFat &&
        <Box style={{ ...css2.gridContainer }}>
          <Box style={ css2.col1 }>
            <Box style={{ ...css2.col1Content, ...css.indent1 }}>
              <Typography variant="h5" style={{ ...css2.h5, ...css.indent1Child }}>{lds.isAbbreviated ? 'Sat.' : 'Saturated'} Fat</Typography>
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col2 }>
            <Box style={ css2.col2Content }>
              {n1.satFat.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.satFat.value ?? '0'}g`}</Box>}
              {n1.satFat.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n1.satFat.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col3 }>
            <Box style={ css2.col3Content }>
              {n2.satFat.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.satFat.value ?? '0'}g`}</Box>}
              {n2.satFat.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n2.satFat.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
        </Box>
      }
      {!lds.isSimplified && showTransFat &&
        <Box style={{ ...css2.gridContainer }}>
          <Box style={ css2.col1 }>
            <Box style={{ ...css2.col1Content, ...css.indent1 }}>
              <Typography variant="h5" style={{ ...css2.h5, ...css.indent1Child }}><i>Trans</i> Fat</Typography>
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col2 }>
            <Box style={ css2.col2Content }>
              {n1.transFat.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.transFat.value ?? '0'}g`}</Box>}
              {n1.transFat.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}></Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col3 }>
            <Box style={ css2.col3Content }>
              {n2.transFat.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.transFat.value ?? '0'}g`}</Box>}
              {n2.transFat.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}></Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
        </Box>
      }
      {!lds.isSimplified && showPolyFat && (isSelected(n1.polyFat) || isSelected(n2.polyFat)) &&
        <Box style={{ ...css2.gridContainer }}>
          <Box style={ css2.col1 }>
            <Box style={{ ...css2.col1Content, ...css.indent1 }}>
              <Typography variant="h5" style={{ ...css2.h5, ...css.indent1Child }}>{lds.isAbbreviated ? 'Poly' : 'Polyunsaturated'} Fat</Typography>
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col2 }>
            <Box style={ css2.col2Content }>
              {n1.polyFat.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.polyFat.value ?? '0'}g`}</Box>}
              {n1.polyFat.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n1.polyFat.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col3 }>
            <Box style={ css2.col3Content }>
              {n2.polyFat.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.polyFat.value ?? '0'}g`}</Box>}
              {n2.polyFat.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n2.polyFat.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
        </Box>
      }
      {!lds.isSimplified && showMonoFat && (isSelected(n1.monoFat) || isSelected(n2.monoFat)) &&
        <Box style={{ ...css2.gridContainer }}>
          <Box style={ css2.col1 }>
            <Box style={{ ...css2.col1Content, ...css.indent1 }}>
              <Typography variant="h5" style={{ ...css2.h5, ...css.indent1Child }}>{lds.isAbbreviated ? 'Mono' : 'Monounsaturated'} Fat</Typography>
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col2 }>
            <Box style={ css2.col2Content }>
              {n1.monoFat.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.monoFat.value ?? '0'}g`}</Box>}
              {n1.monoFat.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n1.monoFat.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col3 }>
            <Box style={ css2.col3Content }>
              {n2.monoFat.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.monoFat.value ?? '0'}g`}</Box>}
              {n2.monoFat.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n2.monoFat.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
        </Box>
      }
      {!lds.isSimplified && showCholesterol &&
        <Box style={{ ...css2.gridContainer }}>
          <Box style={ css2.col1 }>
            <Box style={ css2.col1Content }>
              <Typography variant="h5" style={{ ...css2.h5, ...css.bold}}>{lds.isAbbreviated ? 'Cholest.' : 'Cholesterol'}</Typography>
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col2 }>
            <Box style={ css2.col2Content }>
              {n1.cholesterol.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.cholesterol.value ?? '0'}g`}</Box>}
              {n1.cholesterol.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n1.cholesterol.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col3 }>
            <Box style={ css2.col3Content }>
              {n2.cholesterol.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.cholesterol.value ?? '0'}g`}</Box>}
              {n2.cholesterol.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n2.cholesterol.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
        </Box>
      }      
      <Box style={{ ...css2.gridContainer }}>
        <Box style={ css2.col1 }>
          <Box style={ css2.col1Content }>
            <Typography variant="h5" style={{ ...css2.h5, ...css.bold}}>Sodium</Typography>
            <Divider style={{ ...css.hr, ...css.hr1}} />
          </Box>
        </Box>
        <Box style={ css2.col2 }>
          <Box style={ css2.col2Content }>
            {n1.sodium.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.sodium.value ?? '0'}mg`}</Box>}
            {n1.sodium.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n1.sodium.percent ?? '0'}%`}</Box>}
            &nbsp;
            <Divider style={{ ...css.hr, ...css.hr1}} />
          </Box>
        </Box>
        <Box style={ css2.col3 }>
          <Box style={ css2.col3Content }>
            {n2.sodium.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.sodium.value ?? '0'}mg`}</Box>}
            {n2.sodium.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n2.sodium.percent ?? '0'}%`}</Box>}
            &nbsp;
            <Divider style={{ ...css.hr, ...css.hr1}} />
          </Box>
        </Box>
      </Box>      
      <Box style={{ ...css2.gridContainer }}>
        <Box style={ css2.col1 }>
          <Box style={ css2.col1Content }>
            <Typography variant="h5" style={{ ...css2.h5, ...css.bold}}>Total {lds.isAbbreviated ? 'Carb.' : 'Carbohydrate'}</Typography>
            <Divider style={{ ...css.hr, ...css.hr1}} />
          </Box>
        </Box>
        <Box style={ css2.col2 }>
          <Box style={ css2.col2Content }>
            {n1.carbs.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.carbs.value ?? '0'}g`}</Box>}
            {n1.carbs.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n1.carbs.percent ?? '0'}%`}</Box>}
            &nbsp;
            <Divider style={{ ...css.hr, ...css.hr1}} />
          </Box>
        </Box>
        <Box style={ css2.col3 }>
          <Box style={ css2.col3Content }>
            {n2.carbs.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.carbs.value ?? '0'}g`}</Box>}
            {n2.carbs.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n2.carbs.percent ?? '0'}%`}</Box>}
            &nbsp;
            <Divider style={{ ...css.hr, ...css.hr1}} />
          </Box>
        </Box>
      </Box>  
      {showDietaryFiber &&
        <Box style={{ ...css2.gridContainer }}>
          <Box style={ css2.col1 }>
            <Box style={{ ...css2.col1Content, ...css.indent1 }}>
              <Typography variant="h5" style={{ ...css2.h5, ...css.indent1Child }}>{lds.isAbbreviated ? '' : 'Dietary '}Fiber</Typography>
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col2 }>
            <Box style={ css2.col2Content }>
              {n1.dietaryFiber.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.dietaryFiber.value ?? '0'}g`}</Box>}
              {n1.dietaryFiber.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n1.dietaryFiber.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col3 }>
            <Box style={ css2.col3Content }>
              {n2.dietaryFiber.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.dietaryFiber.value ?? '0'}g`}</Box>}
              {n2.dietaryFiber.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n2.dietaryFiber.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
        </Box>
      }        
      {showTotalSugars &&
        <Box style={{ ...css2.gridContainer }}>
          <Box style={ css2.col1 }>
            <Box style={{ ...css2.col1Content, ...css.indent1 }}>
              <Typography variant="h5" style={{ ...css2.h5, ...css.indent1Child}}>Total Sugars</Typography>
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col2 }>
            <Box style={ css2.col2Content }>
              {n1.totalSugars.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.totalSugars.value ?? '0'}g`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col3 }>
            <Box style={ css2.col3Content }>
              {n2.totalSugars.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.totalSugars.value ?? '0'}g`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
        </Box>
      }  
      {showAddedSugars &&
        <Box style={{ ...css2.gridContainer }}>
          <Box style={ css2.col1 }>
            <Box style={{ ...css2.col1Content, ...css.indent1 }}>
              <Box style={ css.indent1 }>
                <Typography variant="h5" style={{ ...css2.h5, ...css.indent1Child }}>{lds.isAbbreviated ? 'Incl.' : 'Includes'} Added Sugars</Typography>
                <Divider style={{ ...css.hr, ...css.hr1}} />
              </Box>
            </Box>
          </Box>
          <Box style={ css2.col2 }>
            <Box style={ css2.col2Content }>
              {n1.addedSugars.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.addedSugars.value ?? '0'}g`}</Box>}
              {n1.addedSugars.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n1.addedSugars.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col3 }>
            <Box style={ css2.col3Content }>
              {n2.addedSugars.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.addedSugars.value ?? '0'}g`}</Box>}
              {n2.addedSugars.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n2.addedSugars.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
        </Box>
      }        
      {(isSelected(n1.sugarAlcohols) || isSelected(n2.sugarAlcohols)) &&
        <Box style={{ ...css2.gridContainer }}>
          <Box style={ css2.col1 }>
            <Box style={{ ...css2.col1Content, ...css.indent1 }}>
              <Typography variant="h5" style={{ ...css2.h5, ...css2.indent1Child, display:'inline' }}>Sugar Alcohol</Typography>
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col2 }>
            <Box style={ css2.col2Content }>
              {n1.sugarAlcohols.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.sugarAlcohols.value ?? '0'}g`}</Box>}
              {n1.sugarAlcohols.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n1.sugarAlcohols.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col3 }>
            <Box style={ css2.col3Content }>
              {n2.sugarAlcohols.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.sugarAlcohols.value ?? '0'}g`}</Box>}
              {n2.sugarAlcohols.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n2.sugarAlcohols.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
        </Box>
      }     
      {(isSelected(n1.erythritol) || isSelected(n2.erythritol)) &&
        <Box style={{ ...css2.gridContainer }}>
          <Box style={ css2.col1 }>
            <Box style={{ ...css2.col1Content, ...css.indent1 }}>
              <Typography variant="h5" style={{ ...css2.h5, ...css2.indent1Child, display:'inline' }}>Erythritol</Typography>
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col2 }>
            <Box style={ css2.col2Content }>
              {n1.erythritol.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.erythritol.value ?? '0'}g`}</Box>}
              {n1.erythritol.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n1.erythritol.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
          <Box style={ css2.col3 }>
            <Box style={ css2.col3Content }>
              {n2.erythritol.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.erythritol.value ?? '0'}g`}</Box>}
              {n2.erythritol.percentState === 'Visible' && <Box component="span" style={{...css.dv, ...css2.dvBold}}>{`${n2.erythritol.percent ?? '0'}%`}</Box>}
              &nbsp;
              <Divider style={{ ...css.hr, ...css.hr1}} />
            </Box>
          </Box>
        </Box>
      }        
      {/* {(showDietaryFiber || showTotalSugars || showAddedSugars) && <Divider style={{ ...css.hr, ...css.hr1}} /> } */}

      <Box style={{ ...css2.gridContainer }}>
        <Box style={ css2.col1 }>
          <Box style={ css2.col1Content }>
            <Typography variant="h5" style={{ ...css2.h5, ...css.bold}}>Protein</Typography>
            <Divider style={{ ...css.hr, ...css.hr6}} />
          </Box>
        </Box>
        <Box style={ css2.col2 }>
          <Box style={ css2.col2Content }>
            {n1.protein.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n1.protein.value ?? '0'}g`}</Box>}
            &nbsp;
            <Divider style={{ ...css.hr, ...css.hr6}} />
          </Box>
        </Box>
        <Box style={ css2.col3 }>
          <Box style={ css2.col3Content }>
            {n2.protein.amountState === 'Visible' && <Box component="span" style={{ ...css.av, ...css2.av }}>{`${n2.protein.value ?? '0'}g`}</Box>}
            &nbsp;
            <Divider style={{ ...css.hr, ...css.hr6}} />
          </Box>
        </Box>
      </Box>  

      {microNutrients.map((nutrient, idx, arr) => {
        return renderMicroNutrientRow(nutrient.n1, nutrient.n2, idx === (arr.length - 1))
      })} 

      <Divider style={{ ...css.hr, ...css.hr4 }} />
      {!lds.isSimplified && 
        <Box component="span" style={{ ...css.fs6, ...css2.significantSourceText }}>* The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.</Box>
      }
      {lds.isSimplified && significantSourceText && 
        <Box component="span" style={{ ...css.fs6, ...css2.significantSourceText }}>{significantSourceText}</Box>
      }
      {showCitation && 
        <Box component="span" style={{ ...css.fs6, ...css2.significantSourceText }}><DaggerIcon style={{color: '#444', fontSize: '9px', position: 'relative', top:'-3px', width: '9', height:'9'}}/>Contains less than 2 percent of the Daily Value of these nutrients.</Box>
      }      
    </Box>}
  </>)
}

